import React, { useState, useEffect, useContext } from 'react';
import { 
  ExclamationTriangleIcon, 
  ClockIcon, 
  CakeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import { AuthContext } from '../../App';
import apiClient from '../../utils/apiClient';
import { format, differenceInYears, differenceInMonths, differenceInDays, isPast, addMonths } from 'date-fns';
import { de } from 'date-fns/locale';

// Create a typed icon component
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface DogDocument {
  docId: number;
  filename: string;
  created_at: string;
}

interface Dog {
  dog_id: number;
  hundename: string;
  vollstaendiger_name: string;
  rasse: string;
  geburtstag: string;
  chipnummer: string;
  tassonummer: string;
  geschlecht: string;
  kastriert: boolean;
  image_path: string;
  image_path_updated: number;
  hundebox: string;
  haftpflicht: string;
  insurance_name: string;
  valid_insurance: boolean;
  insurance_checked_at: string | null;
  valid_tollwut: boolean;
  tollwut_checked_at: string | null;
  tollwut_end_date: string | null;
  documents: {
    insurance: DogDocument[];
    tollwut: DogDocument[];
  };
}

interface DogIssue {
  type: 'error' | 'warning' | 'info';
  message: string;
  icon: React.ComponentType<any>;
}

const MyDogsStatus: React.FC = () => {
  const { BASE_URL } = useContext(AuthContext);
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const [expandedDog, setExpandedDog] = useState<number | null>(null);

  useEffect(() => {
    fetchDogIssues();
  }, []);

  const fetchDogIssues = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/members/dog_issues');
      if (response.data.success) {
        setDogs(response.data.data || []);
      } else {
        throw new Error(response.data.message || 'Fehler beim Laden der Daten');
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError('Fehler beim Abrufen der Hundedaten');
        console.error('Error fetching dog issues:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthDate: string): { compact: string; full: string } => {
    const birth = new Date(birthDate);
    const now = new Date();
    const years = differenceInYears(now, birth);
    const months = differenceInMonths(now, birth) % 12;

    // Berechne verbleibende Tage nach Jahren und Monaten
    const dateAfterYearsMonths = new Date(birth);
    dateAfterYearsMonths.setFullYear(birth.getFullYear() + years);
    dateAfterYearsMonths.setMonth(birth.getMonth() + months);
    const days = differenceInDays(now, dateAfterYearsMonths);

    // Kompakte Darstellung für Mobile
    const compact = `${years}J ${months}M ${days}T`;

    // Ausgeschriebene Darstellung für Desktop
    const full = `${years} Jahr${years !== 1 ? 'e' : ''}, ${months} Monat${months !== 1 ? 'e' : ''}, ${days} Tag${days !== 1 ? 'e' : ''}`;

    return { compact, full };
  };

  const getDogIssues = (dog: Dog): DogIssue[] => {
    const issues: DogIssue[] = [];
    const today = new Date();
    const oneMonthFromNow = addMonths(today, 1);

    // Check Tollwut (Rabies) vaccination
    if (dog.tollwut_end_date) {
      const tollwutDate = new Date(dog.tollwut_end_date);
      if (isPast(tollwutDate)) {
        issues.push({
          type: 'error',
          message: `Tollwutimpfung abgelaufen seit ${format(tollwutDate, 'dd.MM.yyyy', { locale: de })}`,
          icon: ExclamationTriangleIcon
        });
      } else if (tollwutDate <= oneMonthFromNow) {
        issues.push({
          type: 'warning',
          message: `Tollwutimpfung läuft ab am ${format(tollwutDate, 'dd.MM.yyyy', { locale: de })}`,
          icon: ClockIcon
        });
      }
    }

    // Check for missing Tollwut documents
    if (!dog.documents.tollwut || dog.documents.tollwut.length === 0) {
      issues.push({
        type: 'error',
        message: 'Impfpass-Dokument fehlt',
        icon: ExclamationTriangleIcon
      });
    }

    // Check for missing insurance documents
    if (!dog.documents.insurance || dog.documents.insurance.length === 0) {
      issues.push({
        type: 'warning',
        message: 'Versicherungsnachweis fehlt',
        icon: ClockIcon
      });
    }

    // Check validation status
    if (!dog.valid_tollwut && dog.documents.tollwut && dog.documents.tollwut.length > 0) {
      issues.push({
        type: 'warning',
        message: 'Impfpass-Prüfung ausstehend',
        icon: ClockIcon
      });
    }

    if (!dog.valid_insurance && dog.documents.insurance && dog.documents.insurance.length > 0) {
      issues.push({
        type: 'warning',
        message: 'Versicherungs-Prüfung ausstehend',
        icon: ClockIcon
      });
    }

    return issues;
  };

  const dogsRequiringAttention = dogs.filter((dog) => getDogIssues(dog).length > 0);

  if (loading) {
    return (
      <div>
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="space-y-3 animate-pulse">
            {[1, 2].map((i) => (
              <div key={i} className="bg-gray-50 dark:bg-[#1a2332] rounded-xl p-3 border border-gray-100 dark:border-gray-700/30">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700">
          <div className="bg-error-600 text-white py-3 px-4 flex items-center gap-2 rounded-t-2xl">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Fehler</h3>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-error-500" />
              <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
              <button
                onClick={fetchDogIssues}
                className="ml-auto px-3 py-1 bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300 rounded-lg hover:bg-error-200 dark:hover:bg-error-900/40 text-sm"
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (dogs.length === 0) {
    return (
      <div>
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700">
          <div className="p-8 text-center">
            <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Kein Hund registriert
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Für die Trainingsteilnahme muss dein Hund unter "Meine Hunde" registriert werden.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Pflichtdokumente und Angaben müssen vollständig sein.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Auf der Startseite ist der Bereich eine Aufgabenliste, keine allgemeine
  // Hundeübersicht. Sind alle Angaben und Dokumente in Ordnung, bleibt er
  // vollständig ausgeblendet. Die Hunde sind weiterhin unter „Meine Hunde“
  // erreichbar.
  if (dogsRequiringAttention.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="mb-2 flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
            <DogIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">
            Meine Hunde
          </h2>
        </div>

        <div className="space-y-2">
            {dogsRequiringAttention.map((dog) => {
              const issues = getDogIssues(dog);
              const isExpanded = expandedDog === dog.dog_id;
              const hasError = issues.some(i => i.type === 'error');
              const hasWarning = issues.some(i => i.type === 'warning');

              return (
                <div
                  key={dog.dog_id}
                  className="rounded-lg border border-gray-100 bg-gray-50 p-2.5 dark:border-gray-700/30 dark:bg-[#1a2332]
                            hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600/50
                            transition-all duration-200"
                >
                  <div 
                    className="cursor-pointer"
                    onClick={() => setExpandedDog(isExpanded ? null : dog.dog_id)}
                  >
                    <div className="flex items-center gap-2.5">
                      {/* Dog Image - Round */}
                      <div className="relative h-9 w-9 flex-shrink-0 overflow-hidden rounded-full border-2 border-gray-200 dark:border-gray-600">
                        {!imageErrors[dog.dog_id] ? (
                          <img
                            src={`${BASE_URL}/api/dogs/image-url/${dog.dog_id}?size=S&token=${localStorage.getItem('jwt_token')}&ts=${dog.image_path_updated}`}
                            alt={dog.hundename}
                            className="w-full h-full object-cover"
                            onError={() => setImageErrors(prev => ({ ...prev, [dog.dog_id]: true }))}
                          />
                        ) : (
                          <div className="w-full h-full bg-primary-500 flex items-center justify-center">
                            <DogIcon className="h-5 w-5 text-white/70" />
                          </div>
                        )}
                      </div>

                      {/* Dog Info */}
                      <div className="flex-1 min-w-0">
                        {/* Zeile 1: Name + Badge */}
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                            {dog.hundename}
                          </h4>

                          {/* Status Badge */}
                          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-lg flex-shrink-0 ${
                            hasError ? 'bg-error-50 dark:bg-error-900/20' :
                            hasWarning ? 'bg-warning-50 dark:bg-warning-900/20' :
                            'bg-gray-50 dark:bg-gray-800'
                          }`}>
                            <ExclamationTriangleIcon className={`h-3 w-3 ${
                              hasError ? 'text-error-500' :
                              hasWarning ? 'text-warning-500' :
                              'text-gray-500'
                            }`} />
                            <span className={`text-xs font-medium ${
                              hasError ? 'text-error-700 dark:text-error-300' :
                              hasWarning ? 'text-warning-700 dark:text-warning-300' :
                              'text-gray-700 dark:text-gray-300'
                            }`}>
                              Hinweis
                            </span>
                          </div>
                        </div>

                        {/* Zeile 2: Alter + Rasse */}
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <span className="flex items-center gap-1 flex-shrink-0">
                            <CakeIcon className="h-3 w-3" />
                            {/* Mobile: Kompakte Darstellung */}
                            <span className="sm:hidden">{calculateAge(dog.geburtstag).compact}</span>
                            {/* Desktop: Ausgeschriebene Darstellung */}
                            <span className="hidden sm:inline">{calculateAge(dog.geburtstag).full}</span>
                          </span>
                          <span className="text-gray-400 flex-shrink-0">•</span>
                          <span className="truncate min-w-0">{dog.rasse}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && issues.length > 0 && (
                    <div className="border-t border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 mt-2 p-2 sm:p-3 rounded-b-lg">
                      <div className="space-y-2">
                        {issues.map((issue, idx) => {
                          const IconComponent = issue.icon;
                          return (
                            <div key={idx} className="flex items-start gap-2">
                              <IconComponent 
                                className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                                  issue.type === 'error' ? 'text-error-500' :
                                  issue.type === 'warning' ? 'text-warning-500' :
                                  'text-info-500'
                                }`} 
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {issue.message}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Additional Info */}
                      <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Chip-Nr:</span>
                            <p className="font-mono text-gray-700 dark:text-gray-300">{dog.chipnummer}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Tasso-Nr:</span>
                            <p className="font-mono text-gray-700 dark:text-gray-300">{dog.tassonummer}</p>
                          </div>
                          {dog.insurance_name && (
                            <div className="col-span-2">
                              <span className="text-gray-500 dark:text-gray-400">Versicherung:</span>
                              <p className="text-gray-700 dark:text-gray-300">{dog.insurance_name}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default MyDogsStatus;
